import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import styles from "./Button.module.scss";
import { LayoutUtilProps } from "../../types/props";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the ButtonCompound component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"button">
 */
export type ButtonCompoundProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"button"> & {
    /**
     * The shape of the compound button.
     * @default rounded
     */
    shape?: "pill" | "circular" | "rounded";
  };

/**
 * ButtonCompound component for creating compound button layouts.
 *
 * Features:
 * - Three shape options (pill, circular, rounded)
 * - Transparent background for compound layouts
 * - Supports layout utilities for positioning and spacing
 * - Full accessibility support with proper ARIA attributes
 * - Flexible content support
 * - Consistent styling with other button components
 * - Hover and active state management
 *
 * @example
 * <ButtonCompound shape="circular">
 *   <Avatar name="John Doe" />
 * </ButtonCompound>
 */
export const ButtonCompound = forwardRef<
  HTMLButtonElement,
  ButtonCompoundProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    shape = "rounded",
    type,
    disabled,
    children,
    style,
    ...rest
  } = componentProps;
  const buttonClassNames = cx(
    className,
    styles["button"],
    styles["button-compound"],
    {
      [styles["shape-pill"]]: shape === "pill",
      [styles["shape-circular"]]: shape === "circular",
      [styles["shape-rounded"]]: shape === "rounded",
    },
  );

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <button
      className={buttonClassNames}
      type={type}
      disabled={disabled}
      data-anv="button-compound"
      ref={ref}
      style={styleCombined}
      {...rest}
    >
      {children}
    </button>
  );
});

ButtonCompound.displayName = "ButtonCompound";
